import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { AppShell } from "@/components/crm/AppShell";
import { tokens } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PeriodProvider } from "@/lib/period";

/**
 * Portão das rotas internas.
 *
 * `beforeLoad` só verifica se existe token — a validação de verdade é do
 * servidor. Confiar no cliente para autorizar seria repetir o erro da v1, onde
 * a segurança dependia do que o navegador decidia mostrar.
 */
export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ location }) => {
    if (!tokens.access && !tokens.refresh) {
      throw redirect({ to: "/entrar", search: { redirect: location.href } });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // A sessão pode cair depois do carregamento (refresh recusado). Sem isto, a
  // interface fica de pé mostrando telas vazias e erros de 401.
  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/entrar" });
  }, [loading, user, navigate]);

  if (loading) {
    // Ainda não há AppShell por fora (ele precisa do usuário), por isso a
    // altura é da tela inteira. A marca pulsando é o único "ao vivo" aqui.
    return (
      <div
        role="status"
        className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background"
      >
        <span
          aria-hidden
          className="bg-gradient-brand inline-flex size-12 animate-pulse items-center justify-center rounded-2xl font-display text-sm font-bold tracking-tight text-white shadow-panel"
        >
          RN
        </span>
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <PeriodProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </PeriodProvider>
  );
}
